import { useEffect, useMemo, useRef, useState } from "react";
import { isEqual } from "lodash";

// Market-pattern order (no team names, case-insensitive)
const MARKET_ORDER = [
  /^\d+ over .+ adv$/i,                 // 6/10/15/20 over (sorted by number below)
  /^highest run in over/i,
  /^1st wkt (?!pship|lost).+ adv$/i,
  /^1st 2 wkt/i,
  /^3 wkt or more/i,
  /^total match wkt/i,
  /^total match caught out/i,
  /^top scoring/i,
  / runs open valid$/i,
  /^1st wkt pship boundaries/i,
  / boundaries open valid$/i,
  /^total match boundaries/i,
  /^highest pship boundaries/i,
  /^1st over/i,
  /^1st wkt lost to/i,
  /^h\.m\.b face by/i,
  /^total match four adv$/i,
  /^total match six adv$/i,
  /^total match lbw/i,
  /^total match bowled/i,
  /^total match run out/i,
  /^total match wide/i,
  /^total match extra/i,
  /^total match fifty/i,
  /^total match 30s/i,
  /^total match duckouts/i,
  /^highest pship runs/i,
  /^highest pship balls/i,
  /^total players facing/i,
  /^most balls faced/i,
  /^most runs given/i,
  /^total bowlers giving/i,
  /^total match wicket takers/i,
  /^total match four hitters/i,
  /^total match six hitters/i,
  /^most 4s/i,
  /^most 6s/i,
];
const clean = (s = "") => s.replace(/\s+/g, " ").trim();

const getRank = (name = "") => {
  const n = clean(name);
  const i = MARKET_ORDER.findIndex((re) => re.test(n));
  return i === -1 ? MARKET_ORDER.length : i;
};

// Array.sort is stable: feed order is kept inside each group
const sortMarkets = (list) =>
  [...list].sort((a, b) => {
    const ra = getRank(a?.name);
    const rb = getRank(b?.name);
    if (ra !== rb) return ra - rb;
    // sessions ascending: 6, 10, 15, 20 (BANC/ENGL stay in feed order)
    if (ra === 0) return parseInt(a.name, 10) - parseInt(b.name, 10);
    return 0;
  });

export function useFancyUpdater({
  data,
  type,
  favouriteList,
  fancyHeaderKey,
  headerFilters,
  reorderArray,
}) {
  const [updatedArray, setUpdatedArray] = useState([]);
  const prevData = useRef();

  const normalize = (str = "") => str.toLowerCase();

  const fancyNewUpdate = useMemo(() => {
    if (!data?.length) return [];
    let list = data;
    if (type === "fancyPlus") {
      list = data.filter(({ name = "", categoryType }) => {
        const normalized = normalize(name);
        return (
          /lambi/.test(normalized) ||
          normalized.includes("@1") ||
          categoryType === 6 ||
          normalized.includes("odd run bhav") ||
          normalized.includes("top")
        );
      });
    }
    return sortMarkets(list);
  }, [data, type]);

  const baseArray = useMemo(() => {
    if (favouriteList?.length > 0) {
      return reorderArray(favouriteList, fancyNewUpdate);
    }
    return fancyNewUpdate;
  }, [favouriteList, fancyNewUpdate]);

  const filteredBaseArray = useMemo(() => {
    if (type === "fancy") {
      return baseArray.filter(({ name = "", categoryType }) => {
        const normalized = normalize(name);
        return (
          !normalized.includes("bhav") && // hides toss bhav, run bhav, odd run bhav
          categoryType !== 6
          // "top" exclusion removed so TOP SCORING BATSMAN shows
        );
      });
    }
    return baseArray;
  }, [baseArray, type]);

  const newData = useMemo(() => {
    if (fancyHeaderKey === "All") return filteredBaseArray;
    if (fancyHeaderKey === "extra")
      return filteredBaseArray.filter((item) =>
        normalize(item?.name).includes("adv")
      );
    if (headerFilters?.[fancyHeaderKey]) {
      const keywords = headerFilters[fancyHeaderKey];
      return filteredBaseArray.filter(({ name = "" }) =>
        keywords.some((word) => normalize(name).includes(word))
      );
    }
    return filteredBaseArray.filter(
      (res) => res?.categoryType == fancyHeaderKey
    );
  }, [filteredBaseArray, fancyHeaderKey, headerFilters]);

  useEffect(() => {
    if (!isEqual(prevData.current, newData)) {
      prevData.current = newData;
      setUpdatedArray(newData);
    }
  }, [newData]);

  return updatedArray;
}